import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
    markAllAsRead,
    markAsRead
} from "../../../slices/9-notification/thunk";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import { useNavigate } from "react-router-dom";

dayjs.extend(relativeTime);

const NotificationMenu = ({ onClose, triggerRef, notifications, unreadCount }) => {
    const menuRef = useRef(null);
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const locale = i18n.language === "ar" ? "ar" : "en";
    const navigate = useNavigate();

    // 🔹 Handle closing (mark all as read)
    const handleCloseMenu = () => {
        const hasUnread = notifications?.some((n) => !n.isRead);

        if (hasUnread) {
            dispatch(markAllAsRead());
        }

        onClose();
    };

    // 🔹 Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target) &&
                triggerRef?.current &&
                !triggerRef.current.contains(event.target)
            ) {
                handleCloseMenu();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [notifications, triggerRef]);

    const handleNotificationClick = async (item) => {
        try {
            if (!item?.isRead) {
                await dispatch(markAsRead(item.id)).unwrap();
            }

            if (item?.action_url) {
                setTimeout(() => {
                    window.location.href = item.action_url;
                });
            }

            onClose();
        } catch (error) {
            console.error("Failed to mark as read:", error);
        }
    };

    // console.log(notifications)
    return (
        <div
            ref={menuRef}
            className="absolute z-50 mt-3 border shadow-2xl rtl:left-0 ltr:right-0 w-80 rounded-2xl border-border-light dark:border-border-dark bg-bg-light-secondary dark:bg-bg-dark-secondary backdrop-blur-xl"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 text-lg font-semibold text-black border-b dark:text-white border-border-light dark:border-border-dark">
                <span>{t("notifications.title")}</span>

                {unreadCount > 0 && (
                    <span className="flex items-center justify-center min-w-[22px] h-6 px-2 text-xs font-bold text-white bg-red-500 rounded-full">
                        {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                )}
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-96 no-scrollbar">
                {notifications?.length === 0 && (
                    <div className="px-4 py-6 text-sm text-center text-text-muted-light dark:text-text-muted-dark">
                        {t("notifications.empty")}
                    </div>
                )}

                {notifications?.map((item) => (
                    <div
                        // href={item?.action_url}
                        onClick={() => handleNotificationClick(item)}
                        key={item?.id}
                        className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${!item?.isRead
                            ? "hover:bg-bg-light-primary dark:hover:bg-bg-dark-primary bg-[#00FF9D0D]"
                            : "hover:bg-bg-light-tertiary dark:hover:bg-bg-dark-tertiary"
                            }`}
                    >
                        <img
                            src={item?.image_url}
                            alt="avatar"
                            className="w-10 h-10 rounded-full"
                        />

                        <div className="flex-1">
                            <div className="text-sm text-black dark:text-white">
                                <p className="font-semibold">
                                    {locale === "ar" ? item?.title_AR : item?.title_EN}
                                </p>
                                <p className="text-text-muted-light dark:text-text-muted-dark">
                                    {locale === "ar"
                                        ? item?.message_AR
                                        : item?.message_EN}
                                </p>
                            </div>

                            <p className="mt-1 text-xs text-text-muted-light">
                                {dayjs(item?.createdAt).locale(locale).fromNow()}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 text-center border-t border-border-light dark:border-border-dark">
                <button
                    onClick={() => {
                        handleCloseMenu();
                        navigate("/profile?tab=notification");
                    }}
                    className="text-sm dark:text-primary text-primary-light hover:underline"
                >
                    {t("notifications.viewAll")}
                </button>
            </div>
        </div>
    );
};

export default NotificationMenu;
